import { injectable } from 'inversify';
import { AppService, AppTypes } from 'st-app/Services/AppService';
import { ModelBase, NeoModel, Components } from '@singularsystems/neo-core';
import { CommonParticipantsTypes } from '../CommonParticipantTypes';
import ParticipantModuleConfigLookup from '../Models/ParticipantsConfig/Queries/ParticipantModuleConfigLookup';

@injectable()
@NeoModel
export default class ParticipantConfigService extends ModelBase {
  public constructor(
    public taskRunner = AppService.get(AppTypes.Neo.TaskRunner),
    private participantConfigApiClient = AppService.get(
      CommonParticipantsTypes.ApiClients.ParticipantModuleConfigApiClient,
    ),
  ) {
    super();
  }

  private initialized = false;
  private initializing = false;
  public config: ParticipantModuleConfigLookup | null = null;
  public static readonly defaultBeneficiaryLimit = 5;

  public async initialize() {
    if (!this.initialized && !this.initializing) {
      this.initializing = true;
      try {
        const result = await this.taskRunner.waitFor(
          this.participantConfigApiClient.lookup(),
          { errorHandling: Components.ErrorDisplayType.ThrowError },
        );
        this.config = ParticipantModuleConfigLookup.fromJSObject<ParticipantModuleConfigLookup>(result.data);
        this.initialized = true;
      } catch (error) {
        this.initialized = false;
        this.config = null;
        throw error;
      } finally {
        this.initializing = false;
      }
    }
  }

  public get enableBeneficiaries(): boolean {
    if (this.config === null) {
      return false;
    }
    return this.config.participantRules.enableBeneficiaries;
  }

  public get maximumNumberOfBeneficiaries(): number {
    if (
      this.config === null ||
      this.config.participantRules.maximumNumberOfBeneficiaries == null
    ) {
      return ParticipantConfigService.defaultBeneficiaryLimit;
    }
    return this.config.participantRules.maximumNumberOfBeneficiaries;
  }

  public get isLoaded(): boolean {
    return this.initialized && this.config !== null;
  }

  public get enableCommunicationPreferences(): boolean {
    if (this.config === null) {
      return false;
    }
    return this.config.participantRules.enableCommunicationPreferences;
  }
}
